'use client'

import {
    areColumnWidthsMeasurementsEqual,
    getColumnWidths,
    getTableElement,
} from '../helpers/resize-helpers'
import { Column, ColumnInformation } from '../../../types'
import { useEffect, useMemo, useState } from 'react'

export interface UseStickyColumnWidthsParams {
    tableId: string
    columns: Column[]
    isLoading?: boolean
    hideHeader?: boolean
}

/**
 * Keeps measured widths of sticky header cells in state so multi-column `position: sticky` offsets stay correct.
 * Observes left and right sticky headers; clears measurements when the table has no sticky columns.
 */
export function useStickyColumnWidths({
    tableId,
    columns,
    isLoading,
    hideHeader,
}: UseStickyColumnWidthsParams): ColumnInformation[] | undefined {
    const [columnWidths, setColumnWidths] = useState<ColumnInformation[]>()

    /** Stable while sticky layout is unchanged; avoids re-subscribing when parents pass a new `columns` reference. */
    const stickyColumnsSignature = useMemo(
        () => columns.map((column) => column.sticky ?? '').join('|'),
        [columns],
    )

    const hasStickyColumns = columns.some((column) => column.sticky)

    const mergeMeasuredColumnWidths = (next: ColumnInformation[] | undefined) => {
        setColumnWidths((previous) =>
            areColumnWidthsMeasurementsEqual(previous, next) ? previous : (next ?? previous),
        )
    }

    useEffect(() => {
        if (!hasStickyColumns) {
            mergeMeasuredColumnWidths(getColumnWidths(tableId))
            return undefined
        }

        let pendingAnimationFrameRequestId = 0
        const scheduleMeasure = () => {
            cancelAnimationFrame(pendingAnimationFrameRequestId)
            pendingAnimationFrameRequestId = requestAnimationFrame(() => {
                mergeMeasuredColumnWidths(getColumnWidths(tableId))
            })
        }

        const resizeObserver = new ResizeObserver(() => {
            scheduleMeasure()
        })

        const table = getTableElement(tableId)
        if (table) {
            table
                .querySelectorAll('.table-header-cell--sticky-left, .table-header-cell--sticky-right')
                .forEach((headerCellElement) => resizeObserver.observe(headerCellElement))
        }

        scheduleMeasure()

        return () => {
            cancelAnimationFrame(pendingAnimationFrameRequestId)
            resizeObserver.disconnect()
        }
    }, [tableId, isLoading, hideHeader, stickyColumnsSignature, hasStickyColumns])

    return columnWidths
}
